import dayjs from 'dayjs';
import { fromEnumAsInt, fromEnumAsString, toEnumFromInt, toEnumFromString } from '../../serialization/enum-utils.js';
import { MapUtils } from '../../utility/map-utils.js';
import { TimeZone } from '../../time/timezone.js';
import { VersionRange } from '../core/version-range.js';
import { SerializationContext, DataModelBuilder, SerializedData, isSameData } from '../dto/dto.js';
import { RedeemableCode, ISingleUseRedeemableCode, RedeemableCodeStatus } from '../redeemable-code.js';
import { BusinessModuleRef } from './business-module-ref.js';
import { MembershipRole } from './membership-role.js';

export class StaffInviteCode extends RedeemableCode implements ISingleUseRedeemableCode {
  static readonly TYPE_ID: string = '6cdd8faba58d49b7a0462b5fae03be47';
  static readonly SCHEMA_VERSION: number = 5;

  constructor(
      public readonly uniqueId: string,
      public readonly businessRef: BusinessModuleRef,
      public readonly creationDate: dayjs.Dayjs,
      public readonly expirationDate: dayjs.Dayjs | null,
      public readonly staffRoleId: string,
      public readonly partnerRole: MembershipRole,
      public readonly staffProfileId: string | null,
      public readonly status: RedeemableCodeStatus,
      public readonly usedBy: string | null,
      public readonly usedAt: dayjs.Dayjs | null,
  ) {
    super();
  }

  get $typeId(): string {
    return StaffInviteCode.TYPE_ID;
  }

  get $schemaVersion(): VersionRange {
    return VersionRange.exactly(StaffInviteCode.SCHEMA_VERSION);
  }

  static fromMap(context: SerializationContext, map: SerializedData): StaffInviteCode {
    return new StaffInviteCode(
        MapUtils.get<string>(map, 'uniqueId'),
        BusinessModuleRef.mustDecode(MapUtils.get<string>(map, 'businessRef')),
        context.decodeDate(MapUtils.get<number>(map, 'creationDate')),
        context.decodeDateOrNull(MapUtils.getOrNull<number>(map, 'expirationDate')),
        MapUtils.get<string>(map, 'staffRoleId'),
        toEnumFromInt(MapUtils.get<number>(map, 'partnerRole'), MembershipRole),
        MapUtils.getOrNull<string>(map, 'staffProfileId'),
        toEnumFromString(MapUtils.get<string>(map, '$status'), RedeemableCodeStatus),
        MapUtils.getOrNull<string>(map, 'usedBy'),
        context.decodeDateOrNull(MapUtils.getOrNull<number>(map, 'usedAt')),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'uniqueId': this.uniqueId,
      'businessRef': this.businessRef.encode(),
      'creationDate': context.encodeDate(this.creationDate),
      'expirationDate': context.encodeDateOrNull(this.expirationDate),
      'staffProfileId': this.staffProfileId,
      'staffRoleId': this.staffRoleId,
      'partnerRole': fromEnumAsInt(this.partnerRole),
      '$status': fromEnumAsString(this.status),
      'usedBy': this.usedBy,
      'usedAt': context.encodeDateOrNull(this.usedAt),
    };
  }

  clone(): this {
    return new StaffInviteCode(
        this.uniqueId,
        this.businessRef.clone(),
        TimeZone.cloneDate(this.creationDate),
        TimeZone.cloneDateOrNull(this.expirationDate),
        this.staffRoleId,
        this.partnerRole,
        this.staffProfileId,
        this.status,
        this.usedBy,
        TimeZone.cloneDateOrNull(this.usedAt),
    ) as this;
  }

  isSame(other: this): boolean {
    if(other === this) {
      return true;
    }

    return other.uniqueId === this.uniqueId &&
        isSameData(other.businessRef, this.businessRef) &&
        other.creationDate === this.creationDate &&
        other.expirationDate === this.expirationDate &&
        other.staffRoleId === this.staffRoleId &&
        other.partnerRole === this.partnerRole &&
        other.staffProfileId === this.staffProfileId &&
        other.status === this.status &&
        other.usedBy === this.usedBy &&
        other.usedAt === this.usedAt;
  }

  use(user: string, timestamp: dayjs.Dayjs | null): StaffInviteCode {
    return new StaffInviteCode(
        this.uniqueId,
        this.businessRef.clone(),
        this.creationDate,
        this.expirationDate,
        this.staffRoleId,
        this.partnerRole,
        this.staffProfileId,
        RedeemableCodeStatus.Used,
        user,
        timestamp ?? dayjs(),
    );
  }

  copyWithBusinessRef(businessRef: BusinessModuleRef): this {
    return new StaffInviteCode(
        this.uniqueId,
        businessRef.clone(),
        TimeZone.cloneDate(this.creationDate),
        TimeZone.cloneDateOrNull(this.expirationDate),
        this.staffRoleId,
        this.partnerRole,
        this.staffProfileId,
        this.status,
        this.usedBy,
        TimeZone.cloneDateOrNull(this.usedAt),
    ) as this;
  }
}

export class StaffInviteCodeBuilder extends DataModelBuilder<StaffInviteCode> {
  build(context: SerializationContext, map: SerializedData): StaffInviteCode {
    return StaffInviteCode.fromMap(context, map);
  }

  migrateToCurrentSchemaVersion(context: SerializationContext, map: SerializedData): SerializedData | null {
    const versionRange = MapUtils.getVersionRange(map);
    if(versionRange.contains(StaffInviteCode.SCHEMA_VERSION)) {
      return map;
    }
    if(versionRange.isGreaterThan(StaffInviteCode.SCHEMA_VERSION)) {
      return null; // This data comes from the FUTURE. We have no idea how to migrate it.
    }

    let version = versionRange.to;

    // In this version the [$status] field was added
    if(version < 2) {
      MapUtils.set(map, '$status', RedeemableCodeStatus.Live);
      version = 2;
    }

    // In this version the [usedBy] and [usedAt] fields were added
    if(version < 3) {
      version = 3;
    }

    // In this version [businessId] was replaced with [businessRef]
    if(version < 4) {
      MapUtils.setIfNullOrUndefined(map, 'businessRef', MapUtils.get(map, 'businessId'));
      version = 4;
    }

    // In this version the `userRole` field was replaced with `partnerRole`.
    if(version < 5) {
      version = 5;
      map['partnerRole'] = map['userRole'];
    }

    map['$schema_version'] = VersionRange.encodedExactly(version);
    return map;
  }
}
